import { LinkedList } from "../ds/LinkedList";

interface Waiter<Item> {
    resolve: (item: Item) => void;
    reject: (reason: unknown) => void;
}

/**
 * @remarks
 * - `readSync() === undefined` при пустой очереди неотличим от `write(undefined)`
 * - `Promise.resolve(value)` и `resolve(value)` разворачивают `thenable`,
 *   поэтому `Channel<Promise<T>>` не сохранит промис как значение.
 */
export class Channel<Item> {
    private closed = false;
    private readonly values: LinkedList<Item>;
    private readonly waiters: LinkedList<Waiter<Item>>;

    public constructor() {
        this.values = new LinkedList();
        this.waiters = new LinkedList();
    }

    public write(value: Item): void {
        this.ensureOpen();
        if (this.waiters.notEmpty()) {
            const { resolve } = this.waiters.shift();
            resolve(value);
        } else {
            this.values.push(value);
        }
    }

    public read(): Promise<Item> {
        this.ensureOpen();
        if (this.values.notEmpty()) {
            return Promise.resolve(this.values.shift());
        }
        return new Promise((resolve, reject) => {
            this.waiters.push({ resolve: resolve, reject: reject });
        });
    }

    public readSync(): Item | undefined {
        this.ensureOpen();
        return this.values.shift();
    }

    public close(msg?: string): void {
        this.closed = true;
        this.waiters.forEach(w => w.reject(new Error("Channel: closed", { cause: msg && new Error(msg) })));
        this.waiters.clear();
        this.values.clear();
    }

    private ensureOpen() {
        if (this.closed) {
            throw new Error("Channel: interaction on closed");
        }
    }
}
